import { z } from 'zod';
import { UseMutateFunction, request, useMutation, useQuery } from '@applyboard/ui-utils';
import { refetchType } from '.';

const V2_LOAN_DOCUMENT_PRESIGNED_URL =
  '/api/loans/v2/applications/lead/:id/documents_upload_presigned_url';

const LoanDocumentUploadPresignedUrlResponseSchema = z.object({
  url: z.string().url(),
  fields: z.object({
    bucket: z.string(),
    'X-Amz-Algorithm': z.string(),
    'X-Amz-Credential': z.string(),
    'X-Amz-Date': z.string(),
    'X-Amz-Security-Token': z.string(),
    'X-Amz-Signature': z.string(),
    key: z.string(),
    Policy: z.string(),
  }),
});

export type LoanDocumentUploadPresignedUrlResponse = z.infer<
  typeof LoanDocumentUploadPresignedUrlResponseSchema
>;

export function useGetLoanDocumentUploadPresignedUrl(loanApplicationId: string): {
  presignedUrlDetails: LoanDocumentUploadPresignedUrlResponse | undefined;
  presignedUrlDetailsError: unknown;
  isFetchingPresignedUrlDetails: boolean;
  isFetchedPresignedUrlDetails: boolean;
  refetchPresignedUrlDetails: refetchType;
} {
  const { data, isFetching, isFetched, error, refetch } = useQuery(
    ['get_loan_doc_presigned_url'],
    async () => {
      return request<LoanDocumentUploadPresignedUrlResponse>(
        V2_LOAN_DOCUMENT_PRESIGNED_URL.replace(':id', loanApplicationId),
        {
          schema: LoanDocumentUploadPresignedUrlResponseSchema,
        },
      );
    },
  );

  return {
    presignedUrlDetails: data,
    presignedUrlDetailsError: error,
    isFetchingPresignedUrlDetails: isFetching,
    isFetchedPresignedUrlDetails: isFetched,
    refetchPresignedUrlDetails: refetch,
  };
}

export interface UploadToPresignedUrlParam {
  presignedUrl: string;
  fields: Record<string, string>;
  file: File;
}

export const uploadToPresignedUrlPostRequest = async (
  uploadToPresignedUrlParam: UploadToPresignedUrlParam,
) => {
  const fields = uploadToPresignedUrlParam.fields;
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append('Content-Type', uploadToPresignedUrlParam.file.type);
  formData.append('file', uploadToPresignedUrlParam.file, uploadToPresignedUrlParam.file.name);

  const response = await fetch(uploadToPresignedUrlParam.presignedUrl, {
    method: 'POST',
    body: formData,
  });

  if (response.ok) {
    return response.body;
  } else {
    const message = (await response.text()) ?? 'File upload failed.';
    throw new Error(message);
  }
};

type UseUploadToPresignedUrlResult = {
  uploadToPresignedUrl: UseMutateFunction<unknown, unknown, UploadToPresignedUrlParam, unknown>;
  uploadToPresignedUrlIsLoading: boolean;
  uploadToPresignedUrlError: unknown;
};

export const useUploadToPresignedUrl = (): UseUploadToPresignedUrlResult => {
  const { mutate, isLoading, error } = useMutation(uploadToPresignedUrlPostRequest);

  return {
    uploadToPresignedUrl: mutate,
    uploadToPresignedUrlIsLoading: isLoading,
    uploadToPresignedUrlError: error,
  };
};
